import { useState, useEffect } from 'react';
import Navigation from '../../home/components/Navigation';
import Footer from '../../home/components/Footer';

interface Deduction {
  id: string;
  name: string;
  description: string;
  maxAmount?: string;
  category: string;
}

export default function DeductionFinder() {
  useEffect(() => {
    document.title = "Tax Deduction Finder 2025 | Find All Eligible Deductions - ItsJustTax";
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Discover all tax deductions you qualify for in 2025. Find business expenses, home office deductions, medical expenses, charitable contributions, and more. Maximize your tax savings with our comprehensive deduction finder tool.');
    }
    
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'tax deductions, deduction finder, business expenses, home office deduction, charitable contributions, medical expenses');
    }
  }, []);

  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [filingStatus, setFilingStatus] = useState('single');
  const [scrolled, setScrolled] = useState(false);

  const deductions: Deduction[] = [
    { id: '1', name: 'Mortgage Interest', description: 'Interest paid on home loans up to $750,000', maxAmount: 'Up to $750K loan', category: 'Homeowner' },
    { id: '2', name: 'Property Taxes', description: 'State and local property taxes', maxAmount: '$10,000 cap', category: 'Homeowner' },
    { id: '3', name: 'Charitable Contributions', description: 'Donations to qualified organizations', maxAmount: 'Up to 60% AGI', category: 'General' },
    { id: '4', name: 'Medical Expenses', description: 'Expenses exceeding 7.5% of AGI', maxAmount: 'Above 7.5% AGI', category: 'Medical' },
    { id: '5', name: 'Student Loan Interest', description: 'Interest paid on qualified student loans', maxAmount: 'Up to $2,500', category: 'Education' },
    { id: '6', name: 'Home Office Deduction', description: 'Portion of home used exclusively for business', maxAmount: 'Varies', category: 'Business' },
    { id: '7', name: 'Business Expenses', description: 'Ordinary and necessary business expenses', maxAmount: 'No limit', category: 'Business' },
    { id: '8', name: 'Vehicle Expenses', description: 'Business use of vehicle', maxAmount: '67¢/mile (2026)', category: 'Business' },
    { id: '9', name: 'Health Insurance Premiums', description: 'Self-employed health insurance', maxAmount: 'No limit', category: 'Medical' },
    { id: '10', name: 'Retirement Contributions', description: 'Traditional IRA, 401(k) contributions', maxAmount: '$23,000 (401k)', category: 'Retirement' },
    { id: '11', name: 'Educator Expenses', description: 'Classroom supplies for teachers', maxAmount: 'Up to $300', category: 'Education' },
    { id: '12', name: 'State Income Taxes', description: 'State and local income taxes paid', maxAmount: '$10,000 cap', category: 'General' }
  ];

  const categories = ['All', 'Homeowner', 'Business', 'Medical', 'Education', 'Retirement', 'General'];

  const toggleCategory = (category: string) => {
    if (category === 'All') {
      setSelectedCategories([]);
    } else {
      setSelectedCategories(prev =>
        prev.includes(category)
          ? prev.filter(c => c !== category)
          : [...prev, category]
      );
    }
  };

  const filteredDeductions = selectedCategories.length === 0
    ? deductions
    : deductions.filter(d => selectedCategories.includes(d.category));

  return (
    <div className="min-h-screen bg-white">
      <Navigation scrolled={scrolled} />
      
      <div className="pt-32 pb-20">
        <div className="max-w-6xl mx-auto px-6">
          <div className="mb-8">
            <a href="/" className="inline-flex items-center gap-2 text-teal-600 hover:text-teal-700 mb-4 cursor-pointer">
              <i className="ri-arrow-left-line"></i>
              Back to Home
            </a>
            <h1 className="text-4xl font-bold text-gray-900 mb-4">Deduction Finder</h1>
            <p className="text-lg text-gray-600">Discover all eligible tax deductions for your situation</p>
          </div>

          <div className="bg-white rounded-2xl p-8 shadow-lg mb-8">
            <div className="grid md:grid-cols-2 gap-6 mb-8">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Filing Status</label>
                <select
                  value={filingStatus}
                  onChange={(e) => setFilingStatus(e.target.value)}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-teal-500 focus:outline-none text-lg cursor-pointer"
                >
                  <option value="single">Single</option>
                  <option value="married">Married Filing Jointly</option>
                  <option value="head">Head of Household</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Filter by Category</label>
                <div className="flex flex-wrap gap-2">
                  {categories.map(category => (
                    <button
                      key={category}
                      onClick={() => toggleCategory(category)}
                      className={`px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                        (category === 'All' && selectedCategories.length === 0) || selectedCategories.includes(category)
                          ? 'bg-teal-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {category}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="bg-teal-50 rounded-xl p-6 mb-8">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-teal-600 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-lightbulb-line text-2xl text-white"></i>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Standard vs Itemized Deductions</h3>
                  <p className="text-gray-700 text-sm leading-relaxed">
                    For 2026, the standard deduction is $14,600 (single), $29,200 (married), or $21,900 (head of household). 
                    You should itemize only if your total deductions exceed these amounts.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {filteredDeductions.map(deduction => (
              <div key={deduction.id} className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-teal-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <i className="ri-checkbox-circle-line text-2xl text-teal-600"></i>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-start justify-between mb-2">
                      <h3 className="font-bold text-gray-900 text-lg">{deduction.name}</h3>
                      <span className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-xs font-medium whitespace-nowrap">
                        {deduction.category}
                      </span>
                    </div>
                    <p className="text-gray-600 text-sm mb-3 leading-relaxed">{deduction.description}</p>
                    {deduction.maxAmount && (
                      <div className="flex items-center gap-2 text-sm">
                        <i className="ri-information-line text-teal-600"></i>
                        <span className="text-teal-700 font-medium">{deduction.maxAmount}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-12 bg-gradient-to-br from-teal-600 to-teal-700 rounded-2xl p-8 shadow-lg text-white">
            <h3 className="text-2xl font-bold mb-4">Important Reminders</h3>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="flex gap-4">
                <i className="ri-file-list-3-line text-3xl flex-shrink-0"></i>
                <div>
                  <h4 className="font-semibold mb-2">Keep Records</h4>
                  <p className="text-teal-100 text-sm">Maintain receipts, invoices, and documentation for all deductions you claim</p>
                </div>
              </div>
              <div className="flex gap-4">
                <i className="ri-calendar-check-line text-3xl flex-shrink-0"></i>
                <div>
                  <h4 className="font-semibold mb-2">Timing Matters</h4>
                  <p className="text-teal-100 text-sm">Some deductions must be paid within the tax year to qualify</p>
                </div>
              </div>
              <div className="flex gap-4">
                <i className="ri-shield-check-line text-3xl flex-shrink-0"></i>
                <div>
                  <h4 className="font-semibold mb-2">Eligibility Rules</h4>
                  <p className="text-teal-100 text-sm">Each deduction has specific requirements and income limitations</p>
                </div>
              </div>
              <div className="flex gap-4">
                <i className="ri-user-star-line text-3xl flex-shrink-0"></i>
                <div>
                  <h4 className="font-semibold mb-2">Consult a Professional</h4>
                  <p className="text-teal-100 text-sm">Consider working with a tax professional for complex situations</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}